import { createContext, useState, useContext, useEffect } from 'react';
import { useJob } from './JobContext';
import api from '../services/api.service';

const InventoryPageContext = createContext();

export const useInventoryPage = () => useContext(InventoryPageContext);

export function InventoryPageProvider({ children }) {
  // Shared state for warehouse selection
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedRack, setSelectedRack] = useState('');
  const [selectedBay, setSelectedBay] = useState('');

  // Shared state for job/site selection
  const { jobList, currentJob } = useJob();
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  
  // Other shared data
  const [warehouses, setWarehouses] = useState([]);
  const [racks, setRacks] = useState([]);
  const [bays, setBays] = useState([]);
  const [sites, setSites] = useState([]);
  
  // Scanner state that can be shared between tabs
  const [lastScannedCode, setLastScannedCode] = useState('');
  
  // Initialize warehouses on load
  useEffect(() => {
    console.log('[INVENTORY CONTEXT] useEffect triggered - starting warehouse load');
    const fetchWarehousesAndRacks = async () => {
      try {
        console.log('[INVENTORY CONTEXT] fetchWarehousesAndRacks function started');
        // Fetch warehouses from API
        const warehouseResponse = await api.get('/inventories/warehouses');
        // The API returns { success: true, data: { warehouses: [...] } }
        let warehouseData = [];
        if (warehouseResponse.data?.data?.warehouses) {
          // New structure with nested warehouses property
          warehouseData = warehouseResponse.data.data.warehouses;
        } else if (Array.isArray(warehouseResponse.data)) {
          // Direct array response (legacy)
          warehouseData = warehouseResponse.data;
        } else if (warehouseResponse.data?.data && Array.isArray(warehouseResponse.data.data)) {
          // Wrapped in data property (legacy)
          warehouseData = warehouseResponse.data.data;
        }
        
        console.log('[WAREHOUSE DEBUG] Raw warehouse API response:', warehouseResponse.data);
        console.log('[WAREHOUSE DEBUG] Response is array?', Array.isArray(warehouseResponse.data));
        console.log('[WAREHOUSE DEBUG] Extracted warehouse data:', warehouseData);
        console.log('[WAREHOUSE DEBUG] Sample warehouse structure:', warehouseData?.[0]);
        
        // Ensure warehouseData is an array before filtering
        if (!Array.isArray(warehouseData)) {
          console.warn('[WAREHOUSE DEBUG] warehouseData is not an array:', typeof warehouseData, warehouseData);
          warehouseData = [];
        }
        
        // Filter active warehouses only (API already transforms is_active to isActive)
        const activeWarehouses = warehouseData.filter(w => w.isActive !== false);
        console.log('[WAREHOUSE DEBUG] Active warehouses after filtering:', activeWarehouses);
        
        if (activeWarehouses.length > 0) {
          setWarehouses(activeWarehouses);
          
          // Set default warehouse using real data
          setSelectedWarehouse(activeWarehouses[0].id);
        } else {
          // No warehouses found - set empty state
          console.warn('No active warehouses found from API');
          setWarehouses([]);
          setSelectedWarehouse('');
        }

        // Fetch all racks independently (new architecture)
        try {
          const racksResponse = await api.get('/inventories/racks');
          let racksData = racksResponse.data?.data || racksResponse.data || [];
          
          // Ensure racksData is an array before filtering
          if (!Array.isArray(racksData)) {
            console.warn('[RACKS DEBUG] racksData is not an array:', typeof racksData, racksData);
            racksData = [];
          }
          
          const activeRacks = racksData.filter(r => r.isActive !== false);
          setRacks(activeRacks);
          
          // Set default rack if available
          if (activeRacks.length > 0) {
            setSelectedRack(activeRacks[0].id);
          }
        } catch (err) {
          console.error('Error fetching racks:', err);
          setRacks([]);
          setSelectedRack('');
        }

        // Fetch all bays independently (new architecture)
        try {
          const baysResponse = await api.get('/inventories/bays');
          let baysData = baysResponse.data?.data || baysResponse.data || [];
          
          // Ensure baysData is an array before filtering
          if (!Array.isArray(baysData)) {
            console.warn('[BAYS DEBUG] baysData is not an array:', typeof baysData, baysData);
            baysData = [];
          }
          
          const activeBays = baysData.filter(b => b.isActive !== false);
          setBays(activeBays);
        } catch (err) {
          console.error('Error fetching bays:', err);
          setBays([]);
        }
      } catch (err) {
        console.error('[INVENTORY CONTEXT ERROR] Error fetching warehouses:', err);
        console.error('[INVENTORY CONTEXT ERROR] Error details:', err.response || err.message);
        
        // Set empty state on error - user will need to create warehouses
        setWarehouses([]);
        setRacks([]);
        setBays([]);
        setSelectedWarehouse('');
        setSelectedRack('');
      }
    };
    
    console.log('[INVENTORY CONTEXT] Calling fetchWarehousesAndRacks');
    fetchWarehousesAndRacks();
  }, []);
  
  // Note: Racks are now independent of warehouses, no need to update when warehouse changes
  
  // Initialize job and site selection
  useEffect(() => {
    if (currentJob) {
      setSelectedJobId(currentJob.id);
      setSites(currentJob.sites || []);
      
      // Set default site if available
      if (currentJob.sites && currentJob.sites.length > 0) {
        setSelectedSiteId(currentJob.sites[0].id);
      }
    } else if (jobList.length > 0) {
      setSelectedJobId(jobList[0].id);
      setSites(jobList[0].sites || []);
      
      // Set default site if available
      if (jobList[0].sites && jobList[0].sites.length > 0) {
        setSelectedSiteId(jobList[0].sites[0].id);
      }
    }
  }, [currentJob, jobList]);
  
  // Update sites when job selection changes
  useEffect(() => {
    if (selectedJobId) {
      const job = jobList.find(j => j.id === selectedJobId);
      if (job) {
        setSites(job.sites || []);
        
        // Reset site selection if current site doesn't exist in the new job
        const siteExists = job.sites?.some(s => s.id === selectedSiteId);
        if (!siteExists && job.sites && job.sites.length > 0) {
          setSelectedSiteId(job.sites[0].id);
        } else if (!siteExists) {
          setSelectedSiteId('');
        }
      }
    }
  }, [selectedJobId, jobList]); // REMOVED selectedSiteId to prevent infinite loop
  
  // Get job name for display
  const getJobName = (jobId) => {
    const job = jobList.find(j => j.id === jobId);
    return job ? job.name : jobId;
  };
  
  // Get site name for display
  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : siteId;
  };
  
  // Get rack name for display
  const getRackName = (rackId) => {
    const rack = racks.find(r => r.id === rackId);
    return rack ? rack.name : rackId;
  };
  
  // Get warehouse name for display
  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : warehouseId;
  };

  // Get bay name for display
  const getBayName = (bayId) => {
    const bay = bays.find(b => b.id === bayId);
    return bay ? bay.name : bayId;
  };
  
  // Update sites in the current job
  const updateJobSites = (updatedSites) => {
    setSites(updatedSites);
    
    // Set first site as selected if none is selected
    if ((!selectedSiteId || !updatedSites.some(s => s.id === selectedSiteId)) && updatedSites.length > 0) {
      setSelectedSiteId(updatedSites[0].id);
    }
  };
  
  // Update racks in the current warehouse
  const updateWarehouseRacks = (updatedWarehouse, updatedRacks) => {
    // Update warehouses list
    const updatedWarehouses = warehouses.map(warehouse => 
      warehouse.id === updatedWarehouse
        ? { ...warehouse, racks: updatedRacks }
        : warehouse
    );
    
    setWarehouses(updatedWarehouses);
    setRacks(updatedRacks);
    
    // Set first rack as selected if none is selected
    if ((!selectedRack || !updatedRacks.some(r => r.id === selectedRack)) && updatedRacks.length > 0) {
      setSelectedRack(updatedRacks[0].id);
    }
  };
  
  // Handle location selection from a component
  const handleLocationSelect = (locationData) => {
    if (locationData.warehouse) {
      setSelectedWarehouse(locationData.warehouse);
    }
    
    if (locationData.rack) {
      setSelectedRack(locationData.rack);
    }
  };
  
  // Handle barcode scan from any component
  const handleBarcodeScan = (code) => {
    setLastScannedCode(code);
  };
  
  const value = {
    // Warehouse/rack/bay state
    selectedWarehouse,
    setSelectedWarehouse,
    selectedRack,
    setSelectedRack,
    selectedBay,
    setSelectedBay,
    warehouses,
    racks,
    bays,
    setRacks,

    // Job/site state
    selectedJobId,
    setSelectedJobId,
    selectedSiteId,
    setSelectedSiteId,
    sites,
    
    // Scanner state
    lastScannedCode,
    handleBarcodeScan,
    
    // Helpers
    getJobName,
    getSiteName,
    getRackName,
    getWarehouseName,
    getBayName,
    updateJobSites,
    updateWarehouseRacks,
    handleLocationSelect
  };
  
  return <InventoryPageContext.Provider value={value}>{children}</InventoryPageContext.Provider>;
}